import { useTranslation } from "react-i18next";
import type { DailyRewardDayStatus } from "../../client";
import { REWARD_CONFIG, getRewardIcon } from "./constants";
import { cn } from "../cn";


interface DailyRewardDayCardProps {
  dayData: DailyRewardDayStatus;
  isClaimed: boolean;
  isClaiming: boolean;
  onClaim: (day: number) => void;
}

export default function DailyRewardDayCard({
  dayData,
  isClaimed,
  isClaiming,
  onClaim,
}: DailyRewardDayCardProps) {
  const { t } = useTranslation();
  const config = REWARD_CONFIG[dayData.day];
  const isClaimable = dayData.can_claim;

  const getRewardLabel = () => {
    if (!config) return "";
    if (config.type === "mystery") return "Mystery Box";
    const prefix = "+";
    if (config.type === "exp") {
      return `${prefix}${config.label} EXP`;
    }
    return `${prefix}${config.label}`;
  };

  return (
    <div
      className={cn(
        'box-border flex w-full max-w-full items-center justify-between gap-lg rounded-sm px-xl py-lg',
        isClaimable || isClaimed
          ? 'bg-[linear-gradient(to_right,var(--color-brand),var(--color-deep-blue))]'
          : 'bg-[linear-gradient(to_right,var(--color-disabled-surface),var(--color-disabled-surface-end))]',
      )}
    >
      <div className="flex min-w-0 flex-1 items-center gap-lg overflow-hidden">
        <div className="shrink-0 whitespace-nowrap [font-family:'Rubik_One',sans-serif] text-[14px] leading-[20px] font-normal text-white">
          {t("Day {{day}}", { day: dayData.day })}
        </div>

        <div className="flex w-20 shrink-0 flex-col items-center justify-center gap-0">
          <div className="relative flex h-10 w-10 items-center justify-center">
            <img
              src={getRewardIcon(dayData.day)}
              alt={config?.label || ""}
              className="relative h-full w-full object-contain"
            />
          </div>

          <div className="mt-0 flex h-[14px] w-full items-center justify-center gap-xs">
            <span className="[font-family:Lato,sans-serif] text-[12px] leading-[14px] font-normal text-white">
              {getRewardLabel()}
            </span>
            {config?.type === "pearls" && (
              <img
                src="/ui/shell-icon.png"
                alt=""
                className="h-3 w-3"
              />
            )}
          </div>
        </div>
      </div>

      {isClaimed ? (
        <img
          src="/ui/daily-rewards/daily-rewards-claimed.png"
          alt={t("CLAIMED")}
          className="h-[30px] w-auto shrink-0 object-contain"
        />
      ) : (
        <button
          onClick={() => isClaimable && onClaim(dayData.day)}
          disabled={!isClaimable || isClaiming}
          className={cn(
            'm-0 flex shrink-0 flex-col items-center justify-center whitespace-nowrap rounded-[34.925px] px-3xl py-sm [font-family:"Rubik_One",sans-serif] text-[12px] leading-none font-normal',
            isClaimable
              ? 'border border-accent bg-white text-text-dark shadow-card-raised'
              : 'border-none bg-disabled-surface text-text-muted',
            isClaimable && !isClaiming ? 'cursor-pointer' : 'cursor-not-allowed',
            isClaiming ? 'opacity-60' : isClaimable ? 'opacity-100' : 'opacity-50',
          )}
        >
          <div className="relative flex h-[30px] w-[18px] items-center justify-center">
            <img
              src="/ui/daily-rewards/daily-rewards-pearls-claim.png"
              alt=""
              className="h-[30px] w-[18px] object-contain"
            />
            <span
              className={cn(
                'absolute bottom-0 left-1/2 m-0 -translate-x-1/2 p-0 text-[8px] leading-none whitespace-nowrap',
                isClaimable ? 'text-text-dark' : 'text-text-muted',
              )}
            >
              get
            </span>
          </div>
        </button>
      )}
    </div>
  );
}
